import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LucidLogo } from './components/icons/LucidLogo';
import { LockIcon } from './components/icons/LockIcon';
import { CloseIcon } from './components/icons/CloseIcon';
import CookieModal from './components/CookieModal';

const Terms = () => {
  // Keep the same sources as the Home modal (EN only)
  const legalDocs = useMemo(
    () =>
      ({
        terms: {
          title: 'Terms of Use',
          url: '/legal/en/terms_of_service_29_11_2025.html',
        },
      }) as const,
    []
  );

  const [legalHtml, setLegalHtml] = useState<string>('');
  const [legalLoading, setLegalLoading] = useState(false);
  const [legalError, setLegalError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();

    const load = async () => {
      setLegalLoading(true);
      setLegalError(null);
      try {
        const res = await fetch(legalDocs.terms.url, {
          method: 'GET',
          signal: ctrl.signal,
          headers: { Accept: 'text/html' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        setLegalHtml(html);
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        setLegalHtml('');
        setLegalError('Document could not be loaded. Please try again later.');
      } finally {
        setLegalLoading(false);
      }
    };

    load();
    return () => ctrl.abort();
  }, [legalDocs]);

  return (
    <div
      className="min-h-screen bg-cover bg-center flex items-center justify-center p-4 text-white"
      style={{ backgroundImage: 'url(/bg.jpg)' }}
    >
      <Link
        to="/"
        className="absolute top-6 left-[16px] sm:left-[40px] md:left-[80px] xl:left-[320px]"
      >
        <LucidLogo />
      </Link>

      <div className="w-full max-w-3xl rounded-[16px] p-8 font-pangram text-white text-base leading-6 bg-white/20 backdrop-blur-[40px] supports-backdrop-blur:bg-white/20 supports-backdrop-blur:backdrop-blur-[40px]">
        <div className="flex justify-between items-center mb-4">
          <Link
            to="/"
            aria-label="Back to Home"
            className="text-white text-2xl font-bold hover:text-blue-300"
          >
            ←
          </Link>
          <Link
            to="/"
            aria-label="Close Privacy Page"
            className="text-white text-2xl font-bold hover:text-blue-300"
          >
            <CloseIcon />
          </Link>
        </div>

        <h1 className="text-4xl font-bold font-pangaia mb-6 flex gap-3 flex-nowrap items-center">
          <LockIcon /> {legalDocs.terms.title}
        </h1>

        {/* Loaded HTML document */}
        {legalLoading && (
          <p className="font-pangram text-sm text-white/80">Loading document…</p>
        )}

        {legalError && (
          <div className="space-y-2">
            <p className="font-pangram text-sm text-red-200">{legalError}</p>
            <p className="font-pangram text-sm text-white/70">
              Expected file:{' '}
              <span className="font-mono text-white/80">{legalDocs.terms.url}</span>
            </p>
          </div>
        )}

        {!legalLoading && !legalError && (
          <>
            <div
              className="legal-content text-sm leading-relaxed text-white"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: legalHtml }}
            />
          </>
        )}
      </div>

      <CookieModal />
    </div>
  );
};

export default Terms;
